/**
 * The entry point of the browser build. `src/ui/index.tsx` is still the one that renders the
 * app; this runs the two things that have to happen before it is even loaded.
 *
 * Order is the whole reason this file exists. `src/ui/index.tsx` imports `src/i18n/renderer`,
 * which calls `electron.initialLanguage()` *while being imported* — i18next has to be
 * initialised before the first render or the first frame is painted in the fallback language.
 * So the bridge cannot be installed by the app; it has to be in place before the app's module
 * graph is evaluated, which is what the dynamic import below guarantees and a static one would
 * not.
 */

import { log, silenceConsoleOnProdWeb } from 'src/log';
import { reportCaughtError } from 'src/ui/errorReporting';

import { installWebElectronBridge } from './electronShim';
import { assertWebEnvironmentConfigured } from './environment';
import { startWebErrorReporting } from './errorReporting';

// First, and before the bridge: this is the only point at which nothing has had a chance to print
// yet. The dynamic import below defers the app's whole module graph, so closing the console here
// closes it for everything that follows — including dependencies, which the `log` wrapper never
// reaches.
silenceConsoleOnProdWeb();

installWebElectronBridge();

// Before the app loads, so an error while it does — the misconfigured container below among
// them — reaches the project too.
startWebErrorReporting();

/**
 * A deployment that named no environment, or misspelled one, is a broken container rather than
 * a broken app — and it is broken in a way that must not be papered over, because both
 * available defaults point a deployment at the wrong API.
 *
 * `web/env.sh` refuses to start when a placeholder was left unsubstituted, so this only fires
 * on a value that was supplied and is not one of the three. It is written in English and not
 * translated on purpose: the only person who can act on it is whoever wrote the compose file.
 */
function renderConfigurationError(error: unknown): void {
  const root = document.getElementById('root');
  if (!root) return;

  const message = error instanceof Error ? error.message : String(error);

  root.innerHTML = '';

  const panel = document.createElement('div');
  panel.style.cssText =
    'font: 14px/1.6 system-ui, sans-serif; max-width: 34rem; margin: 20vh auto 0; padding: 0 1.5rem; color: #1a1a1a;';

  const heading = document.createElement('h1');
  heading.style.cssText = 'font-size: 1.1rem; margin: 0 0 .75rem;';
  heading.textContent = 'This deployment is misconfigured';

  const detail = document.createElement('p');
  detail.style.cssText = 'margin: 0 0 .75rem; color: #444;';
  detail.textContent = message;

  const hint = document.createElement('p');
  hint.style.cssText = 'margin: 0; color: #666;';
  // The variable's own name cannot be written here in full: env.sh substitutes every
  // occurrence of it in the built files, so this sentence used to reach the reader as "Set
  // test on the container", naming the value instead of the setting. `DICTARIS_ENVIRONMENT`
  // without the prefix is not a variable name it looks for, so it survives intact.
  hint.textContent =
    "Set DICTARIS_ENVIRONMENT (with the container's APP_PREFIX) to production, demo, test or local, then restart it.";

  panel.append(heading, detail, hint);
  root.appendChild(panel);
}

try {
  assertWebEnvironmentConfigured();
  await import('ui/index');
} catch (error) {
  log.error('Dictaris web could not start', error);
  // A label rather than a translation key: that page is in English, for whoever runs the container.
  reportCaughtError(error, 'misconfiguredDeployment');
  renderConfigurationError(error);
}
